import { useAuth } from '../contexts/AuthContext';
import { Timestamp } from 'firebase/firestore';

export interface SubscriptionState {
    isExpired: boolean;
    daysRemaining: number;
    status: string;
    isLoading: boolean;
}

export const useSubscription = (): SubscriptionState => {
    const { currentLab, user, loading } = useAuth();

    // Whitelist for unlimited access
    const UNLIMITED_EMAILS = [
        'piyushdrago90@gmail.com', // Added based on context or common usage, or just as an example
        // Add your email addresses here
        'piyushdragox@gmail.com'
    ];

    const UNLIMITED_LAB_IDS: string[] = [
        // Add your Lab IDs here if preferred
    ];

    if (loading || !currentLab) {
        return {
            isExpired: false,
            daysRemaining: 0,
            status: 'loading',
            isLoading: true
        };
    }

    // Check for unlimited access
    const isUnlimited =
        (user?.email && UNLIMITED_EMAILS.includes(user.email)) ||
        (currentLab?.id && UNLIMITED_LAB_IDS.includes(currentLab.id));

    if (isUnlimited) {
        return {
            isExpired: false,
            daysRemaining: 3650, // 10 years
            status: 'active',
            isLoading: false
        };
    }

    const status = currentLab.subscriptionStatus || 'trialing'; // Default to trialing for migration

    // If active, no expiration check needed
    if (status === 'active') {
        return {
            isExpired: false,
            daysRemaining: 30, // Arbitrary positive number
            status,
            isLoading: false
        };
    }

    // Check expiration for trial or past_due
    let endsAt = 0;
    if (currentLab.subscriptionEndsAt) {
        if (currentLab.subscriptionEndsAt instanceof Timestamp) {
            endsAt = currentLab.subscriptionEndsAt.toMillis();
        } else if (typeof currentLab.subscriptionEndsAt === 'number') {
            endsAt = currentLab.subscriptionEndsAt;
        } else if (typeof currentLab.subscriptionEndsAt === 'object' && 'toMillis' in currentLab.subscriptionEndsAt) {
            endsAt = (currentLab.subscriptionEndsAt as { toMillis: () => number }).toMillis();
        }
    } else {
        // If no date set (legacy data), give them a default grace period (e.g. 30 days)
        // so they aren't locked out immediately.
        endsAt = Date.now() + 30 * 24 * 60 * 60 * 1000;
    }

    const now = Date.now();
    const msRemaining = endsAt - now;
    const daysRemaining = Math.ceil(msRemaining / (1000 * 60 * 60 * 24));
    const isExpired = msRemaining < 0;

    return {
        isExpired,
        daysRemaining,
        status,
        isLoading: false
    };
};
